import { call, put, takeEvery, fork, select } from 'redux-saga/effects';
import { normalize } from 'normalizr';
import { startSubmit, stopSubmit, reset as resetForm } from 'redux-form';
import { hide as hideModal } from 'redux-modal';

import * as schemas from 'store/common/schemas';
import * as entityActions from 'store/entities/actions';
import * as actionTypes from './action-types';
import * as actions from './actions';
import * as api from 'api/config/companies';
import { selectCompanyByPrettyId } from 'store/features/companies/selectors';
import { selectCompanyAdvisorConnectionById } from 'store/features/company-advisor-connections/selectors';
import callApi from 'store/api/saga';
import { addMessage } from 'store/features/common/flash/actions';
import { messageTypes } from 'store/features/common/flash/builder';
import { confirmSaga as confirm } from 'store/features/common/confirmation-dialog/saga';

import * as advisorsMessages from 'ui/companies/edit/advisors/messages';

function* fetchCompanyAdvisorsConnectionsForAdminByPrettyId({ payload }) {
  const { prettyId } = payload;
  yield put(actions.fetchCompanyAdvisorConnectionsForAdminByPrettyId.start({ prettyId }));

  try {
    const response = yield call(
      callApi,
      api.fetchCompanyAdvisorConnectionsAsAdminByPrettyId({ prettyId })
    );
    const schema = schemas.company;
    const { entities } = normalize({ prettyId, advisors: response }, schema);
    yield put(entityActions.mergeEntities(entities));
    yield put(actions.fetchCompanyAdvisorConnectionsForAdminByPrettyId.success({ prettyId }));
  } catch (error) {
    yield put(
      actions.fetchCompanyAdvisorConnectionsForAdminByPrettyId.failure({ prettyId, error })
    );
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
  }
}

function* watchFetchCompanyAdvisorsConnectionsForAdminByPrettyId() {
  yield takeEvery(
    actionTypes.FETCH_COMPANY_ADVISOR_CONNECTIONS_FOR_ADMIN_BY_PRETTY_ID.REQUEST,
    fetchCompanyAdvisorsConnectionsForAdminByPrettyId
  );
}

function* inviteAdvisorToCompany({ payload: values, meta = {} }) {
  const { name, email, imgUrl, prettyId } = values;
  const { form, modal } = meta;
  yield put(actions.inviteAdvisorToCompany.start(values));
  if (form) yield put(startSubmit(form));

  try {
    const response = yield call(
      callApi,
      api.inviteAdvisorToCompany({ name, email, imgUrl, prettyId })
    );
    response.email = email;

    if (form) yield put(stopSubmit(form));
    if (form) yield put(resetForm(form));
    const schema = schemas.companyAdvisorConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));
    yield put(actions.inviteAdvisorToCompany.success({ advisorConnection: response, prettyId }));
    if (modal) {
      yield put(hideModal(modal));
    }
  } catch (error) {
    if (form) yield put(stopSubmit(form, error.formErrors));
    yield put(actions.inviteAdvisorToCompany.failure({ email, prettyId, error }));
    yield put(
      addMessage({ kind: messageTypes.danger, content: error.message || error.formErrors })
    );
  }
}

function* watchInviteAdvisorToCompany() {
  yield takeEvery(actionTypes.INVITE_ADVISOR_TO_COMPANY.REQUEST, inviteAdvisorToCompany);
}

function* updateAdvisorToCompany({ payload: values, meta = {} }) {
  const { name, imgUrl, prettyId, advisorId } = values;
  const { form, modal } = meta;
  yield put(actions.updateAdvisorToCompany.start(values));
  if (form) yield put(startSubmit(form));

  try {
    let response = yield call(
      callApi,
      api.updateAdvisorToCompany({ name, imgUrl, prettyId, advisorId })
    );

    response = { ...response, id: advisorId };
    if (form) yield put(stopSubmit(form));
    if (form) yield put(resetForm(form));
    const schema = schemas.companyAdvisorConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));
    yield put(actions.updateAdvisorToCompany.success({ advisorConnection: response, prettyId }));
    if (modal) {
      yield put(hideModal(modal));
    }
  } catch (error) {
    if (form) yield put(stopSubmit(form, error.formErrors));
    yield put(actions.updateAdvisorToCompany.failure({ prettyId, error }));
    yield put(
      addMessage({ kind: messageTypes.danger, content: error.message || error.formErrors })
    );
  }
}

function* watchUpdateAdvisorToCompany() {
  yield takeEvery(actionTypes.UPDATE_ADVISOR_TO_COMPANY.REQUEST, updateAdvisorToCompany);
}

function* deleteAdvisorFromCompany({ payload }) {
  const { advisorId, prettyId } = payload;

  const company = yield select(selectCompanyByPrettyId, { prettyId });
  const advisor = yield select(selectCompanyAdvisorConnectionById, { id: advisorId });

  const confirmationMessage = {
    ...advisorsMessages.connectionActions.confirmDelete,
  };
  const confirmed = yield call(confirm, { message: confirmationMessage });
  if (!confirmed) {
    return;
  }

  yield put(actions.deleteAdvisorFromCompany.start({ advisorId, prettyId }));

  try {
    yield call(callApi, api.deleteAdvisorFromCompany({ advisorId, prettyId }));
    const schema = schemas.companyAdvisorConnection;
    yield put(entityActions.removeEntities({ [schema.key]: [advisorId] }));

    const successMessage = {
      ...advisorsMessages.connectionActions.deleted,
      values: {
        companyName: company.companyName,
        advisorName:
          (advisor && advisor.user && advisor.user.name) || (advisor && advisor.email) || '',
      },
    };

    yield put(actions.deleteAdvisorFromCompany.success({ advisorId, prettyId }));
    yield put(addMessage({ kind: messageTypes.success, content: successMessage }));
  } catch (error) {
    yield put(actions.deleteAdvisorFromCompany.failure({ advisorId, prettyId, error }));
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
  }
}

function* watchDeleteAdvisorFromCompany() {
  yield takeEvery(actionTypes.DELETE_ADVISOR_FROM_COMPANY.REQUEST, deleteAdvisorFromCompany);
}

function* updateAdvisorStatus({ payload, meta = {} }) {
  const { advisorId, prettyId, status } = payload;
  const { form } = meta;
  yield put(actions.updateSoftConnectionStatus.start({ advisorId, prettyId }));
  if (form) yield put(startSubmit(form));

  try {
    const response = yield call(
      callApi,
      api.updateSoftConnectionAdminStatus({ advisorId, prettyId, status })
    );
    response.id = advisorId;
    response.company = { id: prettyId };

    if (form) yield put(stopSubmit(form));
    if (form) yield put(resetForm(form));
    const schema = schemas.companyAdvisorConnection;
    const { entities } = normalize(response, schema);
    yield put(entityActions.mergeEntities(entities));
    yield put(
      actions.updateSoftConnectionStatus.success({ advisorConnection: response, prettyId })
    );
  } catch (error) {
    if (form) yield put(stopSubmit(form));
    yield put(
      actions.updateSoftConnectionStatus.failure({ advisorId, prettyId, error: error.message })
    );
    yield put(addMessage({ kind: messageTypes.danger, content: error.message }));
  }
}

function* watchUpdateAdvisorStatus() {
  yield takeEvery(actionTypes.UPDATE_SOFT_CONNECTION_STATUS.REQUEST, updateAdvisorStatus);
}

export default function* companies() {
  yield fork(watchFetchCompanyAdvisorsConnectionsForAdminByPrettyId);
  yield fork(watchInviteAdvisorToCompany);
  yield fork(watchDeleteAdvisorFromCompany);
  yield fork(watchUpdateAdvisorStatus);
  yield fork(watchUpdateAdvisorToCompany);
}



// WEBPACK FOOTER //
// ./src/store/features/admin/companies/advisors/saga.js